<template>
  <div class="page-container">
    <!-- 顶部标题区域 -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="main-title">AI 数字人形象</h1>
        <p class="subtitle">克隆一段视频素材，生成可用于后续合成的视频场景</p>
      </div>
      <el-button type="primary" @click="openCreate" size="large" class="create-btn" round>
        <el-icon>
          <Plus />
        </el-icon> 新建形象
      </el-button>
    </div>

    <!-- 内容区域 -->
    <div class="content-wrapper">
      <!-- 工具条：搜索 + 批量操作 -->
      <div class="toolbar">
        <el-input v-model="keyword" placeholder="搜索形象名称或场景名称" clearable class="kw-input" @keyup.enter="onSearch">
          <template #append>
            <el-button @click="onSearch">搜索</el-button>
          </template>
        </el-input>

        <el-button @click="resetSearch" plain>清空</el-button>

        <el-popconfirm :title="`确认删除选中的 ${selectedIdsArr.length} 个形象吗？`" v-if="selectedIdsArr.length"
          @confirm="doBatchDelete">
          <template #reference>
            <el-button type="danger" :disabled="!selectedIdsArr.length">
              批量删除<span v-if="selectedIdsArr.length">（{{ selectedIdsArr.length }}）</span>
            </el-button>
          </template>
        </el-popconfirm>

        <el-button @click="toggleSelectAll" :disabled="!list.length" plain>
          {{ isAllSelected ? '取消全选' : '全选当前页' }}
        </el-button>
      </div>

      <!-- 卡片列表 -->
      <div class="grid-container" v-loading="loading">
        <el-empty description="暂无形象，点击右上角创建第一个AI数字人形象" v-if="!list.length && !loading" class="empty-state" />

        <div class="card" v-for="item in list" :key="item.id">
          <div class="card-media">
            <!-- 选择框（卡片左上角） -->
            <div class="card-check">
              <el-checkbox :model-value="selectedIds.has(item.id)" @change="toggleOne(item.id)" />
            </div>

            <div class="video-container">
              <video v-if="item.human_video_url" :src="item.human_video_url" preload="metadata" playsinline
                @loadedmetadata="onVideoMeta(item, $event)" :class="item._videoClass || ''" />

              <div class="video-placeholder" v-else>
                <el-icon>
                  <VideoCamera />
                </el-icon>
              </div>
            </div>

            <div class="card-overlay">
              <div class="action-buttons">
                <el-button type="primary" size="small" @click="goCreate(item)" class="action-btn">
                  <el-icon>
                    <MagicStick />
                  </el-icon> 立即创作
                </el-button>
                <el-popconfirm title="确认删除该形象？" @confirm="confirmDelete(item.id)">
                  <template #reference>
                    <el-button size="small" class="action-btn delete-btn">
                      <el-icon>
                        <Delete />
                      </el-icon>
                    </el-button>
                  </template>
                </el-popconfirm>
              </div>
            </div>

            <div class="status-badge" :class="'status-' + item.status">
              {{ statusName(item.status) }}
            </div>
          </div>

          <div class="card-content">
            <div class="card-header">
              <h3 class="card-title" :title="item.human_name">{{ item.human_name }}</h3>
              <el-tag size="small" type="success" effect="plain" class="clone-tag">普通克隆</el-tag>
            </div>

            <div class="card-info">
              <div class="info-item">
                <el-icon>
                  <Location />
                </el-icon>
                <span class="scene-name">{{ item.human_scene }}</span>
              </div>

              <div class="info-item">
                <el-icon>
                  <Document />
                </el-icon>
                <span class="scene-id">场景ID: {{ item.scene_task_id || '—' }}</span>
              </div>
            </div>

            <div class="card-footer">
              <div class="error-message" v-if="item.error_message" :title="item.error_message">
                <el-icon>
                  <Warning />
                </el-icon>
                {{ item.error_message }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 分页 -->
      <div class="pager" v-if="total > 0">
        <el-pagination background layout="total, sizes, prev, pager, next, jumper" :total="total"
          :page-sizes="pageSizes" :page-size="pageSize" :current-page="page" @size-change="handleSizeChange"
          @current-change="handlePageChange" />
      </div>
    </div>

    <!-- 协议弹窗 -->
    <el-dialog v-model="agreementDialogVisible" :title="agreementTitle || '克隆使用协议'" width="720px"
      :close-on-click-modal="false" :close-on-press-escape="false" destroy-on-close>
      <div v-loading="agreementLoading" class="agreement-content">
        <div class="agreement-meta" v-if="agreementUpdatedAt">
          最近更新：{{ agreementUpdatedAt }}
        </div>
        <!-- 默认把 txt 当成纯文本/Markdown 展示；简单起见直接 <pre> -->
        <pre class="agreement-pre" v-if="agreementContent">{{ agreementContent }}</pre>
        <el-empty v-else description="未读取到协议内容" />
      </div>

      <template #footer>
        <el-button @click="agreementDialogVisible = false" :disabled="agreeCountdown > 0">
          关闭{{ agreeCountdown > 0 ? `（${agreeCountdown}s）` : '' }}
        </el-button>
        <el-button type="primary" :disabled="agreeCountdown > 0" @click="confirmAgree">
          {{ agreeCountdown > 0 ? `请阅读（${agreeCountdown}s）` : '我已阅读并同意' }}
        </el-button>
      </template>
    </el-dialog>

    <!-- 新建形象抽屉：上下布局 -->
    <el-drawer v-model="drawerVisible" :with-header="true" size="860px" :close-on-click-modal="false" destroy-on-close
      title="AI 数字人形象克隆" class="custom-drawer">
      <div class="drawer-vertical">
        <!-- 上：表单整块 -->
        <section class="block form-block">
          <el-form ref="formRef" :model="form" :rules="rules" label-width="100px" class="custom-form">
            <!-- 形象名称 -->
            <el-form-item label="形象名称" prop="human_name">
              <el-input v-model="form.human_name" placeholder="支持多个；用中文、英文逗号分隔" size="large">
                <template #append>
                  <el-button @click="insertSep('human_name')" text>插入分隔符</el-button>
                </template>
              </el-input>
            </el-form-item>

            <el-form-item label="克隆模式">
              <el-tag type="success" effect="plain" class="mode-tag">普通克隆</el-tag>
            </el-form-item>

            <!-- 场景名称 -->
            <el-form-item label="场景名称" prop="human_scene">
              <el-input v-model="form.human_scene" placeholder="支持多个；用中文、英文逗号分隔" size="large">
                <template #append>
                  <el-button @click="insertSep('human_scene')" text>插入分隔符</el-button>
                </template>
              </el-input>
            </el-form-item>

            <!-- 视频直链 + 上传卡片 -->
            <el-form-item label="视频素材" prop="human_video_url" class="video-url-item">
              <div class="uploader-panel">
                <!-- 顶部：输入与标签 -->
                <div class="uploader-header">
                  <el-input v-model="form.human_video_url" type="textarea" :rows="2" class="mono-input"
                    placeholder="粘贴视频直链；支持多个，用英文逗号 , 分隔" />
                  <div class="uploader-hint">

                    <div class="uploader-price" v-if="!configLoading">
                      单价：<b>{{ cloneUnitPrice }}</b> 点 / 每个视频
                    </div>

                    <el-icon>
                      <Document />
                    </el-icon>
                    示例：https://your-bucket.oss-xx.aliyuncs.com/path/video.mp4
                  </div>

                  <div v-if="uploadedUrls.length" class="url-chips">
                    <el-tag v-for="(u, i) in uploadedUrls" :key="u" closable @close="removeUrl(i)" type="info"
                      effect="plain">
                      {{ u }}
                    </el-tag>
                  </div>
                </div>

                <!-- 底部：左上传区 + 右提示区 -->
                <div class="uploader-body">
                  <!-- 左：拖拽上传卡片，支持多选 -->
                  <el-upload class="drop-card" drag multiple :auto-upload="false" :show-file-list="false"
                    accept=".mp4,.mov,.m4v,.webm,.flv,.f4v,.mkv,.rm,.rmvb,.3gp,.3g2,.wmv,.avi,.asf,.mpg,.mpeg,.ts"
                    :on-change="onPickLocalVideo">
                    <div class="drop-inner">
                      <div class="drop-icon"><el-icon>
                          <Upload />
                        </el-icon></div>
                      <div class="drop-title">将视频拖到此处</div>
                      <div class="drop-sub">或 <span class="link">点击选择</span>（可多选）</div>
                      <div class="drop-meta">系统会自动校验 <b>5~120 秒</b>，并上传到 OSS 填入直链</div>
                    </div>
                  </el-upload>

                  <!-- 右：小贴士 + 上传队列 -->
                  <div class="uploader-aside">
                    <div class="aside-title">
                      <el-icon>
                        <InfoFilled />
                      </el-icon>
                      上传说明
                    </div>
                    <ul class="aside-list">
                      <li>建议分辨率 ≥ 720p，码率≥4Mbps</li>
                      <li>支持常见容器：mp4</li>
                      <li>每个素材建议 10~60 秒更稳</li>
                      <li>可一次选择多个文件批量上传</li>
                    </ul>

                    <div v-if="queue.length" class="queue">
                      <div class="queue-title">上传进度</div>
                      <div v-for="q in queue" :key="q.id" class="queue-item">
                        <span class="q-name" :title="q.name">{{ q.name }}</span>
                        <el-progress :percentage="q.percent" :status="q.status" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </el-form-item>

            <el-form-item class="agreement-item">
              <el-checkbox :model-value="agree" @change="onAgreeBoxChange">
                我已阅读并同意
                <el-link type="primary" :underline="false" @click.prevent="openAgreement(false)">
                  《克隆使用协议》
                </el-link>
              </el-checkbox>
            </el-form-item>


            <el-form-item class="form-actions">
              <el-button :loading="submitting" type="primary" :disabled="!agree" @click="submit" size="large"
                class="submit-btn" round>
                <el-icon>
                  <MagicStick />
                </el-icon> 克隆形象（{{ costDisplayText }}）
              </el-button>
              <el-button @click="drawerVisible = false" size="large" round>取消</el-button>
            </el-form-item>
          </el-form>
        </section>

        <!-- 下：示例与规范（左右布局） -->
        <section class="block guide-block">
          <div class="guide-grid">
            <!-- 左：示例视频 -->
            <div class="sample-card">
              <div class="sample-header">
                <span class="sample-title">示例视频</span>
                <el-tooltip content="参考此视频格式和内容进行录制" placement="top">
                  <el-icon>
                    <InfoFilled />
                  </el-icon>
                </el-tooltip>
              </div>
              <div class="sample-body">
                <video class="sample-video" src="https://api.niwaai.com/video_exp.mp4" controls playsinline
                  preload="metadata" />
              </div>
            </div>

            <!-- 右：建议/忌讳 -->
            <div class="tips-container">
              <div class="tips-group ok-tips">
                <div class="tips-header">
                  <el-icon class="tips-icon">
                    <CircleCheckFilled />
                  </el-icon>
                  <span class="tips-title">建议</span>
                </div>
                <ul class="tips-list">
                  <li>提交 <b>5~120秒</b> 的原视频素材</li>
                  <li>人脸占比不超过画面 <b>1/2</b></li>
                  <li>可静坐、走路、骑车，实景/绿幕均可</li>
                  <li>尽量高清且光线明亮均匀</li>
                  <li>每帧都要露脸，侧脸幅度不要过大</li>
                  <li>句与句之间自然停顿，闭口</li>
                  <li>手势自然，不要遮挡嘴巴</li>
                </ul>
              </div>

              <div class="tips-group bad-tips">
                <div class="tips-header">
                  <el-icon class="tips-icon">
                    <CircleCloseFilled />
                  </el-icon>
                  <span class="tips-title">忌讳</span>
                </div>
                <ul class="tips-list">
                  <li>素材过长导致失败或极慢</li>
                  <li>人脸占比过大/过小</li>
                  <li>嘴巴与面部 3/4 以上在镜头内</li>
                  <li>明显阴影或过曝/模糊</li>
                  <li>频繁转移视线、频繁环顾</li>
                  <li>说话无停顿，假笑一直露齿</li>
                  <li>动作过于频繁或镜头过抖</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import axios from 'axios'
import Cookies from 'js-cookie'
import config from '@/config.js'
import {
  Plus,
  VideoCamera,
  MagicStick,
  Delete,
  Location,
  Document,
  Warning,
  Upload,
  InfoFilled,
  CircleCheckFilled,
  CircleCloseFilled
} from '@element-plus/icons-vue'
import { getUploadSignature, uploadToOSS, beforeUpload, getVideoDuration } from '@/utils/uploadUtils'

export default {
  name: 'HumanClone',
  components: {
    Plus,
    VideoCamera,
    MagicStick,
    Delete,
    Location,
    Document,
    Warning,
    Upload,
    InfoFilled,
    CircleCheckFilled,
    CircleCloseFilled
  },
  data() {
    return {
      loading: false,
      list: [],
      drawerVisible: false,
      processedUids: new Set(),   // 标记已处理的文件
      submitting: false,
      queue: [],                  // 上传进度队列 [{id,name,percent,status}]
      uploadedUrls: [],           // 已上传成功的直链列表
      configLoading: false,
      cloneUnitPrice: 0,   // ← 接口返回的克隆单价（数字）
      agree: false,
      // 协议弹窗相关
      agreementDialogVisible: false,
      agreementLoading: false,
      agreementTitle: '',
      agreementUpdatedAt: '',
      agreementContent: '',
      agreeCountdown: 0,   // 剩余秒数
      agreeTimer: null,    // 计时器
      form: {
        human_name: '',
        human_type: 1,
        human_scene: '客厅',
        human_video_url: ''
      },
      rules: {
        human_name: [{ required: true, message: '请输入名称', trigger: 'blur' }],
        human_scene: [{ required: true, message: '请输入场景', trigger: 'blur' }],
        human_video_url: [{ required: true, message: '请填写视频地址', trigger: 'blur' }]
      },

      // 搜索/批量选择/分页
      keyword: '',
      selectedIds: new Set(), // 选中的 id 集合
      page: 1,
      pageSize: 12,
      total: 0,
      pageSizes: [12, 24, 48, 96],
      isSearching: false
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    },
    selectedIdsArr() {
      return Array.from(this.selectedIds)
    },
    isAllSelected() {
      return this.list.length > 0 && this.list.every(it => this.selectedIds.has(it.id))
    },
    // 当前填写的“视频直链”里有多少个有效 URL（中/英文逗号都支持）
    videoCount() {
      return this.splitByAnyComma(this.form.human_video_url).length
    },
    // 总费用（保留两位小数）
    totalCloneCost() {
      const total = (this.cloneUnitPrice || 0) * (this.videoCount || 0)
      return Number.isFinite(total) ? total.toFixed(2) : '0.00'
    },
    // 展示文本（更友好：显示“单价 × 数量 = 总计/免费”）
    costDisplayText() {
      if (!this.videoCount) return '消耗积分'
      if (!this.cloneUnitPrice) return `预计消耗 0 点（本次免费，${this.videoCount} 个）`
      return `预计消耗 ${this.totalCloneCost} 点（${this.cloneUnitPrice} × ${this.videoCount}）`
    }
  },
  mounted() {
    this.fetchCloneUnitPrice().finally(() => {
      this.loadData()
    })
  },

  methods: {
    // —— 列表请求（自动根据是否搜索决定接口 & 参数）—— //
    async loadData() {
      try {
        this.loading = true
        const body = this.isSearching
          ? {
            action: 'search',
            store_id: this.userInfo.id,
            keyword: this.keyword.trim(),
            page: this.page,
            page_size: this.pageSize
          }
          : {
            action: 'list',
            store_id: this.userInfo.id,
            page: this.page,
            page_size: this.pageSize
          }
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/human_clone.php`, body)
        this.list = data?.data || []
        this.total = data?.pagination?.total || 0
        // 如果当前页没有数据且不是第一页，自动回退一页
        if (!this.list.length && this.page > 1) {
          this.page = this.page - 1
          await this.loadData()
          return
        }
        this.clearSelection()
      } catch (e) {
        this.$message.error('获取列表失败')
      } finally {
        this.loading = false
      }
    },

    onVideoMeta(item, e) {
      try {
        const video = e?.target
        const w = video?.videoWidth || 0
        const h = video?.videoHeight || 0
        if (!w || !h) { item._videoClass = ''; return }

        // 设定阈值：更稳健地区分横/竖/近似正方
        const ratio = w / h
        if (ratio > 1.1) {
          item._videoClass = 'video-landscape'   // 横版：继续 cover
        } else if (ratio < 0.9) {
          item._videoClass = 'video-portrait'    // 竖版：contain + 顶对齐
        } else {
          item._videoClass = 'video-square'      // 近似方形：contain
        }
      } catch (err) {
        console.warn('onVideoMeta error:', err)
        item._videoClass = ''
      }
    },

    // 拦截用户直接勾选复选框：需要先阅读 3 秒
    onAgreeBoxChange(val) {
      if (val && !this.agree) {
        this.openAgreement(true) // 来自“勾选复选框”的触发
      } else if (!val) {
        this.agree = false
      }
    },

    // 打开协议弹窗：fromCheckbox = 是否来源于点击复选框
    // 打开协议弹窗
    async openAgreement() {
      try {
        this.agreementDialogVisible = true
        this.agreementLoading = true
        const { data } = await axios.get(`${config.BASE_URL}/shop/api/get_clone_agreement.php`)
        if (data?.code === 200 && data?.data) {
          this.agreementTitle = data.data.title || '克隆使用协议'
          this.agreementUpdatedAt = data.data.updated_at || ''
          this.agreementContent = data.data.content || ''
        } else {
          this.agreementTitle = '克隆使用协议'
          this.agreementUpdatedAt = ''
          this.agreementContent = ''
        }
      } catch (e) {
        this.$message.error('读取使用协议失败')
        this.agreementContent = ''
      } finally {
        this.agreementLoading = false
        this.startAgreeCountdown(3)
      }
    },


    // 启动倒计时（秒）
    startAgreeCountdown(sec = 3) {
      this.clearAgreeTimer()
      this.agreeCountdown = sec
      this.agreeTimer = setInterval(() => {
        if (this.agreeCountdown > 0) {
          this.agreeCountdown--
        }
        if (this.agreeCountdown <= 0) {
          this.clearAgreeTimer()
        }
      }, 1000)
    },

    clearAgreeTimer() {
      if (this.agreeTimer) {
        clearInterval(this.agreeTimer)
        this.agreeTimer = null
      }
    },

    // 点击“我已阅读并同意”
    confirmAgree() {
      if (this.agreeCountdown > 0) return
      this.agree = true
      this.agreementDialogVisible = false
    },
    async fetchCloneUnitPrice() {
      try {
        this.configLoading = true
        const { data } = await axios.get(`${config.BASE_URL}/shop/api/get_digital_human_config.php`)
        // 兼容后端返回结构：{"code":200,"data":{"digital_human_clone_num":"2.00", ...}}
        if (data?.code === 200 && data?.data?.digital_human_clone_num != null) {
          const n = parseFloat(data.data.digital_human_clone_num)
          this.cloneUnitPrice = Number.isFinite(n) ? n : 0
        } else {
          this.cloneUnitPrice = 0
        }
      } catch (err) {
        console.error('fetchCloneUnitPrice failed:', err)
        this.cloneUnitPrice = 0
      } finally {
        this.configLoading = false
      }
    },

    // —— 搜索 —— //
    async onSearch() {
      this.isSearching = !!this.keyword.trim()
      this.page = 1
      await this.loadData()
    },
    async resetSearch() {
      this.keyword = ''
      this.isSearching = false
      this.page = 1
      await this.loadData()
    },

    // —— 分页 —— //
    async handlePageChange(p) {
      this.page = p
      await this.loadData()
    },
    async handleSizeChange(ps) {
      this.pageSize = ps
      this.page = 1
      await this.loadData()
    },

    // —— 选择 —— //
    clearSelection() {
      this.selectedIds.clear()
    },
    toggleOne(id) {
      if (this.selectedIds.has(id)) this.selectedIds.delete(id)
      else this.selectedIds.add(id)
    },
    toggleSelectAll() {
      if (!this.list.length) return
      if (this.isAllSelected) {
        this.selectedIds.clear()
      } else {
        this.list.forEach(it => this.selectedIds.add(it.id))
      }
    },

    // —— 批量删除 —— //
    async doBatchDelete() {
      if (!this.selectedIds.size) return
      try {
        this.loading = true
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/human_clone.php`, {
          action: 'batch_delete',
          store_id: this.userInfo.id,
          ids: Array.from(this.selectedIds)
        })
        if (data.success) {
          this.$message.success('批量删除成功')
          // 删除后如果本页空了，回退一页再取
          await this.loadData()
        } else {
          this.$message.error(data.error || '批量删除失败')
        }
      } catch (e) {
        this.$message.error('批量删除失败')
      } finally {
        this.loading = false
      }
    },

    // —— 单个删除 —— //
    async confirmDelete(id) {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/human_clone.php`, {
          action: 'delete',
          id,
          store_id: this.userInfo.id
        })
        if (data.success) {
          this.$message.success('已删除')
          if (this.selectedIds.has(id)) this.selectedIds.delete(id)
          await this.loadData()
        } else {
          this.$message.error(data.error || '删除失败')
        }
      } catch (e) {
        console.info('删除失败：', e)
      }
    },

    statusName(s) {
      if (s === 2) return '失败'
      if (s === 1) return '正常'
      return '—'
    },

    // 插入标准分隔符（英文逗号，智能补空格）
    insertSep(field) {
      const val = (this.form[field] ?? '').toString()
      const endsWithComma = /[，,]\s*$/.test(val)
      this.form[field] = endsWithComma ? `${val} ` : `${val}${val ? ', ' : ''}`
    },

    // 同时支持中文/英文逗号
    splitByAnyComma(str) {
      return (str || '')
        .split(/[，,]/g)
        .map(s => s.trim())
        .filter(Boolean)
    },

    openCreate() {
      this.drawerVisible = true
      this.agree = false
      this.uploadedUrls = []
      this.queue = []
      this.processedUids.clear()
      this.form = { human_name: '', human_type: 1, human_scene: '客厅', human_video_url: '' }
      this.$nextTick(() => this.$refs.formRef?.clearValidate?.())
    },

    // 本地上传 -> 校验 -> 上传到 OSS -> 回填直链
    async onPickLocalVideo(uploadFile) {
      try {
        const uid = uploadFile?.uid
        const file = uploadFile?.raw || uploadFile
        if (!file) return

        if (uid && this.processedUids.has(uid)) return
        if (uid) this.processedUids.add(uid)

        const q = {
          id: uid || `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          name: file.name || '未命名视频',
          percent: 0,
          status: undefined
        }
        this.queue.push(q)

        const ok = await beforeUpload(file, { type: 'video' })
        if (!ok) { q.status = 'exception'; return }
        q.percent = 10

        const seconds = await getVideoDuration(file)
        if (isNaN(seconds) || seconds < 5 || seconds > 120) {
          this.$message.error(`${q.name} 时长需在 5~120 秒，当前约 ${isNaN(seconds) ? '未知' : seconds.toFixed(1)} 秒`)
          q.status = 'exception'
          return
        }
        q.percent = 45

        const sign = await getUploadSignature('video')
        const res = await uploadToOSS(file, sign)
        q.percent = 90

        if (res?.success && res.url) {
          this.uploadedUrls.push(res.url)
          const manual = this.form.human_video_url
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
          const total = Array.from(new Set([...manual, ...this.uploadedUrls]))
          this.form.human_video_url = total.join(',')
          q.percent = 100
          q.status = 'success'
          this.$message.success(`上传成功：${q.name}`)
        } else {
          q.status = 'exception'
          this.$message.error(`上传失败：${q.name}`)
        }
      } catch (err) {
        console.error(err)
        this.$message.error(err?.message || '上传失败')
      }
    },

    removeUrl(idx) {
      this.uploadedUrls.splice(idx, 1)
      const manual = this.splitByAnyComma(this.form.human_video_url)
      const remained = manual.filter(u => this.uploadedUrls.includes(u))
      this.form.human_video_url = remained.join(',')
    },

    async submit() {
      this.$refs.formRef.validate(async ok => {
        if (!ok) return
        this.submitting = true
        try {
          const urls = this.splitByAnyComma(this.form.human_video_url)
          const names = this.splitByAnyComma(this.form.human_name)
          const scenes = this.splitByAnyComma(this.form.human_scene)

          const payload = {
            action: 'add',
            store_id: this.userInfo.id,
            human_name: names.length > 1 ? names : (names[0] || ''),
            human_scene: scenes.length > 1 ? scenes : (scenes[0] || ''),
            human_type: 1,
            human_video_url: urls.length > 1 ? urls : (urls[0] || ''),
            callback_url: 'https://www.baidu.com'
          }

          const { data } = await axios.post(`${config.BASE_URL}/shop/api/human_clone.php`, payload)
          if (data.success) {
            this.$message.success('提交成功，任务已创建')
            this.drawerVisible = false
            // 保持当前模式刷新（搜索/普通、分页不变）
            await this.loadData()
          } else {
            this.$message.error(data.error || '提交失败')
          }
        } catch (e) {
          this.$message.error('提交失败')
        } finally {
          this.submitting = false
        }
      })
    },

    // 修改goCreate方法
    goCreate(row) {
      if (row.scene_task_id) {
        // 跳转到合成页面并传递形象ID
        this.$router.push({
          path: '/human_clone/humanrecord',
          query: { cloneId: row.scene_task_id }
        });
      } else {
        this.$message.warning('该形象尚未准备好，无法进行创作');
      }
    }
  }
}
</script>

<style scoped>
.page-container {
  padding: 24px;
  background: #f8fafc;
  min-height: 100vh;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* 顶部标题区域美化 */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 28px 32px;
  border-radius: 16px;
  color: white;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
}

.header-content {
  flex: 1;
}

.header-content h1 {
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 700;
}

.header-content p {
  margin: 0;
  opacity: 0.9;
  font-size: 16px;
}

.main-title {
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
}

.subtitle {
  margin: 0;
  color: #f7f8fb;
  font-size: 14px;
}

.create-btn {
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
  padding: 10px 20px;
}

/* 工具条 */
.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.kw-input {
  width: 320px;
}

/* 内容区域 */
.content-wrapper {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

/* 卡片网格布局优化 */
.grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 24px;
}

.empty-state {
  grid-column: 1 / -1;
  padding: 60px 0;
}

/* 卡片样式 */
.card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  position: relative;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
}

.card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #667eea, #764ba2);
  z-index: 1;
}

.card-media {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.video-container {
  width: 100%;
  height: 100%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-container video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-placeholder {
  color: #9ca3af;
  font-size: 48px;
}

/* 选择框（卡片左上角） */
.card-check {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 2;
  background: rgba(255, 255, 255, .85);
  border-radius: 8px;
  padding: 4px 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .08);
}

.card-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, .7), transparent 60%);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  opacity: 0;
  transition: opacity .25s ease;
  padding: 16px;
}

.card:hover .card-overlay {
  opacity: 1;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.action-btn {
  border-radius: 6px;
}

.delete-btn {
  background: rgba(239, 68, 68, .92);
  border: none;
  color: #fff;
}

.delete-btn:hover {
  background: rgb(239, 68, 68);
}

.status-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #fff;
}

.status-1 {
  background: #10b981;
}

.status-2 {
  background: #ef4444;
}

.status-0 {
  background: #6b7280;
}

.card-content {
  padding: 16px;
}

.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;
}

.card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 70%;
}

.clone-tag {
  border: none;
  font-weight: 500;
}

.card-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #6b7280;
}

.card-footer {
  border-top: 1px solid #f3f4f6;
  padding-top: 12px;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #ef4444;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 抽屉整体：上下布局 */
.custom-drawer {
  --el-drawer-padding-primary: 0;
}

.drawer-vertical {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 18px 24px 24px;
}

.block {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, .05);
}

.form-block {
  padding: 20px 20px 8px;
}

/* 表单 */
.custom-form :deep(.el-form-item__label) {
  font-weight: 500;
  color: #374151;
}

.mode-tag {
  border: none;
  padding: 8px 12px;
  font-weight: 500;
}

.video-url-item :deep(.el-form-item__content) {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.small-hint {
  color: #9aa3af;
  font-size: 12px;
  margin-top: -6px;
}

.url-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.upload-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.upload-hint {
  font-size: 12px;
  color: #6b7280;
}

.agreement-item {
  margin-top: 8px;
}

.form-actions {
  margin-top: 6px;
}

.submit-btn {
  box-shadow: 0 4px 12px rgba(59, 130, 246, .25);
}

/* 下块：示例与规范（左右布局） */
.guide-block {
  padding: 16px;
}

.guide-grid {
  display: grid;
  grid-template-columns: 1fr 1.1fr;
  gap: 16px;
}

.sample-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #eef2f7;
}

.sample-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: #f9fafb;
  border-bottom: 1px solid #eef2f7;
}

.sample-title {
  font-weight: 600;
  color: #1f2937;
}

.sample-body {
  padding: 12px;
  background: #0f111a;
}

.sample-video {
  width: 100%;
  border-radius: 8px;
  display: block;
}

.tips-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.tips-group {
  background: #fff;
  border-radius: 12px;
  padding: 14px 16px;
  border: 1px solid #eef2f7;
}

.tips-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.tips-title {
  font-weight: 600;
  font-size: 16px;
}

.ok-tips .tips-icon {
  color: #10b981;
}

.ok-tips .tips-title {
  color: #10b981;
}

.bad-tips .tips-icon {
  color: #ef4444;
}

.bad-tips .tips-title {
  color: #ef4444;
}

.tips-list {
  margin: 0;
  padding-left: 18px;
  color: #6b7280;
  line-height: 1.8;
}

.tips-list b {
  color: #4b5563;
  font-weight: 600;
}

/* 响应式 */
@media (max-width: 1024px) {
  .guide-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .page-container {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .grid-container {
    grid-template-columns: 1fr;
  }
}

/* —— Uploader Panel —— */
.uploader-panel {
  border: 1px solid #eef2f7;
  border-radius: 12px;
  padding: 14px;
  background: #fbfdff;
}

.uploader-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* 直链输入用等宽字体，显得更“技术” */
.mono-input :deep(textarea) {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  letter-spacing: .2px;
}

.uploader-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #9aa3af;
  font-size: 12px;
  margin-top: -2px;
}

.url-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}

/* 下半块：上传 + 说明 */
.uploader-body {
  display: grid;
  grid-template-columns: 1.1fr .9fr;
  gap: 14px;
  margin-top: 12px;
}

.drop-card {
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  background: #fff;
  transition: border-color .2s, box-shadow .2s;
}

.drop-card:hover {
  border-color: #93c5fd;
  box-shadow: 0 8px 24px rgba(59, 130, 246, .08);
}

.drop-inner {
  padding: 22px 16px;
  text-align: center;
}

.drop-icon {
  font-size: 28px;
  color: #60a5fa;
  margin-bottom: 4px;
}

.drop-title {
  font-weight: 600;
  color: #1f2937;
}

.drop-sub {
  color: #6b7280;
  margin-top: 4px;
}

.drop-sub .link {
  color: #2563eb;
  cursor: pointer;
}

.drop-meta {
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
}

.uploader-aside {
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  padding: 12px 14px;
}

.aside-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 8px;
}

.aside-list {
  margin: 0;
  padding-left: 18px;
  color: #6b7280;
  line-height: 1.7;
  font-size: 13px;
}

.aside-list li {
  margin-bottom: 4px;
}

/* 上传队列区域（默认随内容高度；若文件很多可改为固定高+滚动） */
.queue {
  margin-top: 10px;
  max-height: 260px;
  overflow-y: auto;
}

.queue-title {
  font-weight: 600;
  margin-bottom: 6px;
  color: #374151;
}

.queue-item {
  margin-bottom: 10px;
}

.q-name {
  display: block;
  font-size: 12px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 4px;
}

/* 分页 */
.pager {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

/* 窄屏自适应 */
@media (max-width: 720px) {
  .uploader-body {
    grid-template-columns: 1fr;
  }
}

.uploader-price {
  color: #374151;
  font-size: 13px;
  margin-top: 2px;
}

.agreement-content {
  max-height: 60vh;
  overflow: auto;
  padding-right: 4px;
}

.agreement-meta {
  color: #6b7280;
  font-size: 12px;
  margin-bottom: 8px;
}

.agreement-pre {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.7;
  font-size: 14px;
  color: #374151;
}

/* 让卡片媒体区用固定比例（默认 16:9），更稳定 */
.card-media {
  position: relative;
  /* 删除固定高度，改为比例；如需维持原高度，可保留 height，但 aspect-ratio 更灵活 */
  height: auto;
  aspect-ratio: 16 / 9;
  overflow: hidden;
}

/* 容器：默认黑底，防止 letterbox 很突兀可自定义颜色 */
.video-container {
  width: 100%;
  height: 100%;
  background: #0b0b0b;
  /* 比 #f3f4f6 更适合播放留边 */
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 视频元素默认策略（兜底） */
.video-container video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* 默认铺满 */
  object-position: center;
  /* 默认居中 */
  border-radius: 0;
}

/* 横版：维持 cover（可略过，留作显式） */
.video-container video.video-landscape {
  object-fit: cover;
  object-position: center;
}

/* 竖版：展示全貌，避免裁掉头部；顶对齐更少裁头（contain 不会裁，但留边在两侧） */
.video-container video.video-portrait {
  object-fit: contain;
  /* 不裁剪，左右留边 */
  object-position: top center;
  /* 顶部优先，避免头部被出画 */
  background: #000;
  /* 视频自身背景，和容器一致 */
}

/* 方形：一般也用 contain 展示完整内容 */
.video-container video.video-square {
  object-fit: contain;
  object-position: center;
  background: #000;
}

/* 悬浮时出现按钮的覆盖层，和原样式兼容（可选美化） */
.card-overlay {
  pointer-events: none;
  /* 默认不拦点击 */
}

.card-overlay .action-buttons {
  pointer-events: auto;
  /* 按钮可点 */
}

/* 可选：状态角标更易读 */
.status-badge {
  backdrop-filter: saturate(180%) blur(6px);
  background: rgba(0, 0, 0, .45);
}

/* 可选：占位图美化（保持你原有图标，但统一底色） */
.video-placeholder {
  color: #9ca3af;
  font-size: 48px;
}
</style>